import { generateBank } from "@/data/questionGenerator";
import { PART_KEYS } from "@/data/parts";
import { api } from "@/lib/api";

const BANK_KEY = "lexv_bank_v2";
const AI_KEY = "lexv_ai_items";
const HIST_KEY = "lexv_history";
const SET_KEY = "lexv_settings";

let cache = null;
let access = "demo";
// Free demo sample counts per section (server sends only these; mirrored for the offline generator)
export const DEMO_LIMITS = { A: 10, B: 10, C: 10, F: 10, G: 10, D: 3, E: 3, H: 3, I: 3 };
const SPLIT = { 10: { easy: 4, medium: 3, hard: 3 }, 3: { easy: 1, medium: 1, hard: 1 } };
const demoSlice = (b) => Object.fromEntries(Object.entries(b).map(([k, xs]) => [k, Object.entries(SPLIT[DEMO_LIMITS[k]] || {}).flatMap(([t, n]) => xs.filter((x) => x.difficulty === t).slice(0, n))]));
export const getAccess = () => access;
const TIER_KEYS = ["easy", "medium", "hard"];
// Procedural fallback has no tiers: thirds for length-sorted Part B, round-robin elsewhere
const withTiers = (b) => Object.fromEntries(Object.entries(b).map(([k, xs]) => [k, xs.map((x, i) => (x.difficulty ? x : { ...x, difficulty: k === "B" ? TIER_KEYS[Math.min(2, Math.floor((i * 3) / xs.length))] : TIER_KEYS[i % 3] }))]));
const read = (k, d) => { try { return JSON.parse(localStorage.getItem(k)) ?? d; } catch { return d; } };

export const getBaseBank = () => {
  if (cache) return cache;
  const stored = read(BANK_KEY, null);
  if (stored && PART_KEYS.every((k) => (stored[k] || []).length >= 100)) return (cache = withTiers(stored));
  cache = withTiers(generateBank());
  try { localStorage.setItem(BANK_KEY, JSON.stringify(cache)); } catch { /* quota */ }
  return cache;
};

export const resetBank = () => { localStorage.removeItem(AI_KEY); return getBaseBank(); };

const SERVER_KEY = "lexv_bank_server";
// Loads the seeded server bank (500+/350+ per part); falls back to cache, then the local generator
export const initBank = async () => {
  try {
    const { data: meta } = await api.get("/bank/meta", { timeout: 6000 });
    access = meta?.access === "full" ? "full" : "demo";
    if (!meta?.version) return;
    const cached = read(SERVER_KEY, null);
    if (cached?.version === meta.version && (cached.access || "full") === access && cached.parts) { cache = withTiers(cached.parts); return; }
    const { data } = await api.get("/bank", { timeout: 30000 });
    cache = withTiers(data.parts);
    localStorage.removeItem(BANK_KEY);
    try { localStorage.setItem(SERVER_KEY, JSON.stringify(data)); } catch { /* storage full: keep in memory */ }
  } catch {
    const cached = read(SERVER_KEY, null);
    if (cached?.parts && cached.access === "demo") cache = withTiers(cached.parts);
  }
};

export const getAiItems = () => read(AI_KEY, {});

const VALID = {
  A: (x) => typeof x.text === "string", B: (x) => typeof x.text === "string", E: (x) => typeof x.text === "string", G: (x) => typeof x.text === "string",
  C: (x) => Array.isArray(x.chunks) && x.chunks.length === 3 && typeof x.answer === "string",
  D: (x) => Array.isArray(x.turns) && x.turns.length >= 2 && x.question && Array.isArray(x.answers),
  F: (x) => typeof x.sentence === "string" && x.sentence.includes("_") && Array.isArray(x.answers),
  H: (x) => typeof x.text === "string" && Array.isArray(x.keywords),
  I: (x) => typeof x.text === "string" && Array.isArray(x.keywords),
};

export const addAiItems = (part, items) => {
  const ai = getAiItems();
  const clean = items.filter((x) => x && VALID[part](x)).map((x, i) => ({
    ...x,
    ...(part === "D" ? { answers: x.answers.map((a) => String(a).toLowerCase()), turns: x.turns.map((t, j) => ({ speaker: j % 2 ? "B" : "A", text: t.text || String(t) })) } : {}),
    ...(part === "F" ? { answers: x.answers.map((a) => String(a).toLowerCase()) } : {}),
    ...(part === "I" && !x.title ? { title: "AI Passage" } : {}),
    id: `${part}-AI-${Date.now().toString(36)}-${i}`, ai: true,
  }));
  ai[part] = [...(ai[part] || []), ...clean];
  localStorage.setItem(AI_KEY, JSON.stringify(ai));
  return clean.length;
};

export const getBank = () => {
  const b = getBaseBank(), ai = getAiItems();
  if (access !== "full") return demoSlice(b);
  return Object.fromEntries(PART_KEYS.map((k) => [k, [...b[k], ...(ai[k] || [])]]));
};

export const getSettings = () => ({ examLength: "official", voiceURI: "", ...read(SET_KEY, {}) });
export const saveSettings = (s) => localStorage.setItem(SET_KEY, JSON.stringify({ ...getSettings(), ...s }));

export const getHistory = () => read(HIST_KEY, []);
export const saveHistoryEntry = (e) => {
  const h = [e, ...getHistory().filter((x) => x.id !== e.id)].slice(0, 25);
  localStorage.setItem(HIST_KEY, JSON.stringify(h));
};
export const mergeHistory = (remote) => {
  const map = new Map(getHistory().map((x) => [x.id, x]));
  remote.forEach((x) => map.set(x.id, x));
  const all = [...map.values()].sort((a, b) => b.date.localeCompare(a.date)).slice(0, 25);
  localStorage.setItem(HIST_KEY, JSON.stringify(all));
  return all;
};
export const clearHistory = () => localStorage.removeItem(HIST_KEY);

const ACTIVE_KEY = "lexv_active_exam";
const DRILL_KEY = "lexv_drill";
export const getActiveExam = () => read(ACTIVE_KEY, null);
export const saveActiveExam = (s) => localStorage.setItem(ACTIVE_KEY, JSON.stringify(s));
export const clearActiveExam = () => localStorage.removeItem(ACTIVE_KEY);
export const getDrill = () => read(DRILL_KEY, null);
export const saveDrill = (d) => localStorage.setItem(DRILL_KEY, JSON.stringify(d));
export const clearDrill = () => localStorage.removeItem(DRILL_KEY);
